// Cave84 — Bottom nav (Phase 0 Day 3, mobile-only)
// 5 slots: Dashboard / Inventory / Consumed / Wishlist / More.
// "More" opens a sheet with the secondary destinations (Vintage Charts +
// Investment/Import/Cellars/Settings for owners). Hidden on md+ where TopBar carries main nav.

import { useState, useEffect } from 'react';
import type { ReactElement } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { useAuthStore } from '@/stores/auth.store';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  icon: ReactElement;
}

const PRIMARY: ReadonlyArray<NavItem> = [
  {
    to: '/',
    label: 'Dashboard',
    end: true,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 12 12 4l9 8" />
        <path d="M5 10v10h14V10" />
      </svg>
    )
  },
  {
    to: '/inventory',
    label: 'Inventory',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 4h14v4H5z" />
        <path d="M6 8v12h12V8" />
        <path d="M10 12h4" />
      </svg>
    )
  },
  {
    to: '/consumed',
    label: 'Consumed',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 3h6v5a3 3 0 0 1-3 3 3 3 0 0 1-3-3V3z" />
        <path d="M12 11v8" />
        <path d="M9 21h6" />
      </svg>
    )
  },
  {
    to: '/wishlist',
    label: 'Wishlist',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 20s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.5-7 10-7 10z" />
      </svg>
    )
  }
];

function navLinkClass({ isActive }: { isActive: boolean }) {
  return clsx(
    'flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2 transition-colors',
    isActive ? 'text-gold' : 'text-text-tertiary hover:text-text-secondary'
  );
}

export function BottomNav() {
  const role = useAuthStore((s) => s.userDoc?.role);
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        aria-label="Primary"
        className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border-subtle bg-base/95 backdrop-blur-sm pb-[env(safe-area-inset-bottom)]"
      >
        <div className="flex items-stretch h-14">
          {PRIMARY.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={navLinkClass}
            >
              <span className="w-5 h-5">{item.icon}</span>
              <span className="text-[11px] tracking-wide">{item.label}</span>
            </NavLink>
          ))}
          <button
            type="button"
            onClick={() => setMoreOpen(true)}
            className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1 py-2 text-text-tertiary hover:text-text-secondary transition-colors"
            aria-haspopup="dialog"
            aria-expanded={moreOpen}
          >
            <span className="w-5 h-5">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="5" cy="12" r="1.4" />
                <circle cx="12" cy="12" r="1.4" />
                <circle cx="19" cy="12" r="1.4" />
              </svg>
            </span>
            <span className="text-[11px] tracking-wide">More</span>
          </button>
        </div>
      </nav>

      {moreOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="More navigation"
          className="md:hidden fixed inset-0 z-40"
        >
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute bottom-0 inset-x-0 rounded-t-xl border-t border-border-medium bg-surface-1 pb-[env(safe-area-inset-bottom)]">
            <div className="flex justify-center pt-2">
              <span className="w-10 h-1 rounded-full bg-border-medium" aria-hidden="true" />
            </div>
            <p className="px-5 pt-3 pb-1 smallcaps text-text-tertiary">More</p>
            <ul className="py-2">
              <li>
                <button
                  type="button"
                  onClick={() => navigate('/vintage-charts')}
                  className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                >
                  Vintage Charts
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => navigate('/analytics')}
                  className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                >
                  Analytics
                </button>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => navigate('/special-occasions')}
                  className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                >
                  ★ Special Occasions
                </button>
              </li>
              {role === 'owner' && (
                <>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/prices/investment')}
                      className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                    >
                      Investment
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/import')}
                      className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                    >
                      Import invoice
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/cellars')}
                      className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                    >
                      Cellars
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => navigate('/settings')}
                      className="w-full text-left px-5 py-3 text-text-primary hover:bg-surface-2 transition-colors"
                    >
                      Settings
                    </button>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}
